// WebRTCContext.js
import React, { createContext, useState, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";

import {
  getFirestore,
  collection,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  onSnapshot,
  addDoc,
  getDocs,
  deleteDoc
} from 'firebase/firestore';

// Firebase Initialization (only once)
const firebaseConfig = {
  apiKey: "AIzaSyBLTq_B-F18LMdZkodanoQIAsfUKMLGUCU",
  authDomain: "my-webrtc-midi-app.firebaseapp.com",
  projectId: "my-webrtc-midi-app",
  storageBucket: "my-webrtc-midi-app.firebasestorage.app",
  messagingSenderId: "262868990569",
  appId: "1:262868990569:web:c3eda2c726fdcac0f8c0f8"
};

// DEBUGGING ONLY
if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-restricted-globals
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}

const app = initializeApp(firebaseConfig);

const appCheck = initializeAppCheck(app, {
  provider: new ReCaptchaV3Provider('6Ldknd4qAAAAAMpXXPkdc6HebAPmUpE0WmkZ4xz7'),
  isTokenAutoRefreshEnabled: true
});

const db = getFirestore(app);

// WebRTC Configuration
const configuration = {
  iceServers: [
    {
      urls: ["stun:stun1.l.google.com:19302", "stun:stun2.l.google.com:19302"],
    },
  ],
  iceCandidatePoolSize: 10,
};

export const WebRTCContext = createContext(null);

export const WebRTCProvider = ({ children }) => {
  // State variables for button disabled statuses
  const [openMediaDisabled, setOpenMediaDisabled] = useState(false);
  const [openAudioOnlyDisabled, setOpenAudioOnlyDisabled] = useState(false);
  const [createRoomDisabled, setCreateRoomDisabled] = useState(true);
  const [joinRoomDisabled, setJoinRoomDisabled] = useState(true);
  const [hangupDisabled, setHangupDisabled] = useState(true);

  // State for room and role
  const [roomId, setRoomId] = useState('');

  // Refs for DOM elements (to be attached by the consuming component)
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const roomIdInputRef = useRef(null);

  // Refs for WebRTC objects and media streams
  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(new MediaStream());

  // Ref to hold the data channel callback function
  const dataChannelCallbackRef = useRef(null);

  // Function to register a callback to be executed when data is received
  const registerDataChannelCallback = (callback) => {
    dataChannelCallbackRef.current = callback;
  };

  // Handle media (audio and video)
  const handleOpenMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = remoteStreamRef.current;
      }
      console.log("Local media stream obtained.");
      setCreateRoomDisabled(false);
      setJoinRoomDisabled(false);
      setOpenMediaDisabled(true);
      setOpenAudioOnlyDisabled(true);
      setHangupDisabled(false);
    } catch (error) {
      console.error("Error accessing media devices.", error);
    }
  };

  // Handle audio only
  const handleOpenAudioOnly = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = remoteStreamRef.current;
      }
      console.log("Local audio-only stream obtained.");
    } catch (error) {
      console.error("Error accessing audio devices.", error);
    }
  };

  // Create a new room
  const createRoom = async () => {
    const pc = new RTCPeerConnection(configuration);
    peerConnectionRef.current = pc;

    localStreamRef.current.getTracks().forEach(track => {
      pc.addTrack(track, localStreamRef.current);
    });

    // Create a data channel (for MIDI data)
    const channel = pc.createDataChannel("midiChannel");
    channel.onopen = () => console.log("Data Channel Open!");
    channel.onmessage = (event) => {
      console.log("WebRTC - Received channel data:", event.data);
      // Invoke the registered callback, if any.
      if (dataChannelCallbackRef.current) {
        dataChannelCallbackRef.current(event.data);
      }
    };
    dataChannelRef.current = channel;
    dataChannelRef.current.binaryType = 'arraybuffer';
    console.log("Data channel created from createRoom()");

    // Create a new room in Firestore.
    const roomRef = doc(collection(db, "rooms"));
    setRoomId(roomRef.id);
    const callerCandidatesCollection = collection(db, "rooms", roomRef.id, "callerCandidates");

    pc.onicecandidate = (event) => {
      if (!event.candidate) return;
      addDoc(callerCandidatesCollection, event.candidate.toJSON());
      // console.log("Added caller ICE candidate:", event.candidate);
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const roomWithOffer = {
      offer: {
        type: offer.type,
        sdp: offer.sdp,
      },
    };

    try {
      await setDoc(roomRef, roomWithOffer);
    } catch (error) {
      // Check for specific error codes if needed
      if (error.code === 'permission-denied') {
        console.error('Insufficient permissions:', error);
        // Handle the error, e.g., show a user-friendly message or redirect
      } else {
        console.error('Unexpected error occurred:', error);
        // Handle other potential errors
      }
    }
    console.log("Room created with offer:", roomRef.id);

    const calleeCandidatesCollection = collection(db, "rooms", roomRef.id, "calleeCandidates");
    onSnapshot(calleeCandidatesCollection, (snapshot) => {
      snapshot.docChanges().forEach(change => {
        if (change.type === "added") {
          const candidate = new RTCIceCandidate(change.doc.data());
          // console.log("Adding callee ICE candidate:", candidate);
          pc.addIceCandidate(candidate);
        }
      });
    });

    onSnapshot(roomRef, async (snapshot) => {
      const data = snapshot.data();
      if (!pc.currentRemoteDescription && data?.answer) {
        const answerDescription = new RTCSessionDescription(data.answer);
        await pc.setRemoteDescription(answerDescription);
        console.log("Remote description set (answer):", answerDescription);
      }
    });

    pc.ontrack = (event) => {
      event.streams[0].getTracks().forEach(track => {
        remoteStreamRef.current.addTrack(track);
      });
    };
  };

  // Join an existing room
  const joinRoom = async (roomIdValue) => {
    if (!roomIdValue) {
      alert("Please enter a room ID.");
      return;
    }
    const roomRef = doc(db, "rooms", roomIdValue);
    const roomSnapshot = await getDoc(roomRef);
    if (!roomSnapshot.exists()) {
      alert("Room does not exist!");
      return;
    }
    setRoomId(roomIdValue);
    const pc = new RTCPeerConnection(configuration);
    peerConnectionRef.current = pc;

    localStreamRef.current.getTracks().forEach(track => {
      pc.addTrack(track, localStreamRef.current);
    });

    pc.ondatachannel = (event) => {
      dataChannelRef.current = event.channel;
      dataChannelRef.current.onopen = () => console.log("Data Channel Open!");
      dataChannelRef.current.onmessage = (e) => console.log("Received data:", e.data);
      console.log("Data channel created from joinRoom()");
    };

    const calleeCandidatesCollection = collection(db, "rooms", roomIdValue, "calleeCandidates");
    pc.onicecandidate = (event) => {
      if (!event.candidate) return;
      addDoc(calleeCandidatesCollection, event.candidate.toJSON());
      // console.log("Added callee ICE candidate:", event.candidate);
    };

    pc.ontrack = (event) => {
      event.streams[0].getTracks().forEach(track => {
        remoteStreamRef.current.addTrack(track);
      });
    };

    const roomData = roomSnapshot.data();
    await pc.setRemoteDescription(new RTCSessionDescription(roomData.offer));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    const roomWithAnswer = {
      answer: {
        type: answer.type,
        sdp: answer.sdp,
      },
    };
    await updateDoc(roomRef, roomWithAnswer);
    console.log("Answered room:", roomIdValue);

    const callerCandidatesCollection = collection(db, "rooms", roomIdValue, "callerCandidates");
    onSnapshot(callerCandidatesCollection, (snapshot) => {
      snapshot.docChanges().forEach(change => {
        if (change.type === "added") {
          const candidate = new RTCIceCandidate(change.doc.data());
          // console.log("Adding caller ICE candidate:", candidate);
          pc.addIceCandidate(candidate);
        }
      });
    });
  };

  // Hang up and clean up
  const hangUp = async () => {
    console.log("Hanging up...");
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (remoteStreamRef.current) {
      remoteStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (roomId) {
      const roomRef = doc(db, "rooms", roomId);
      const calleeCandidatesCollection = collection(db, "rooms", roomId, "calleeCandidates");
      const callerCandidatesCollection = collection(db, "rooms", roomId, "callerCandidates");

      const calleeCandidatesSnapshot = await getDocs(calleeCandidatesCollection);
      calleeCandidatesSnapshot.forEach(async candidate => {
        await deleteDoc(candidate.ref);
      });

      const callerCandidatesSnapshot = await getDocs(callerCandidatesCollection);
      callerCandidatesSnapshot.forEach(async candidate => {
        await deleteDoc(candidate.ref);
      });

      await deleteDoc(roomRef);
    }
    setRoomId('');
    peerConnectionRef.current = null;
    dataChannelRef.current = null;
    localStreamRef.current = null;
    remoteStreamRef.current = new MediaStream();
  };

  // Helper function to send data over the data channel
  const sendData = (data) => {
    if (dataChannelRef.current && dataChannelRef.current.readyState === 'open') {
      dataChannelRef.current.send(new Uint8Array(data).buffer);
    } else {
      console.warn("Data channel is not open");
    }
  };

  const value = {
    localVideoRef,
    remoteVideoRef,
    roomIdInputRef,
    roomId,
    openMediaDisabled,
    openAudioOnlyDisabled,
    createRoomDisabled,
    joinRoomDisabled,
    hangupDisabled,
    handleOpenMedia,
    handleOpenAudioOnly,
    createRoom,
    joinRoom,
    hangUp,
    sendData,
    registerDataChannelCallback
  };

  return (
    <WebRTCContext.Provider value={value}>
      {children}
    </WebRTCContext.Provider>
  );
};

export default WebRTCProvider;